import { ApolloProvider } from "@apollo/client";
import { clientNeoAuth } from "@neonumy/api-clients";
import * as Sentry from "@sentry/browser";
import { createRoot } from "react-dom/client";
import { hotjar } from "react-hotjar";
import App from "./App";
import * as serviceWorker from "./serviceWorker";

const CI_COMMIT_SHORT_SHA = import.meta.env.VITE_CI_COMMIT_SHORT_SHA;

let isReloadingForNewBuild = false;

// After a new deploy, chunk hashes change and lazy imports of stale chunks
// fail (the server falls back to index.html). Reload once to fetch the new build.
// preventDefault() makes the import resolve to undefined, so only call it when
// reloading: otherwise the real error has to reach the ErrorBoundary.
window.addEventListener("vite:preloadError", event => {
  const reloadKey = "vite-preload-error-reload";
  // sessionStorage stores strings: an unset SHA would never match and reload forever.
  const buildId = String(CI_COMMIT_SHORT_SHA);
  if (sessionStorage.getItem(reloadKey) === buildId) return;

  event.preventDefault();
  isReloadingForNewBuild = true;
  sessionStorage.setItem(reloadKey, buildId);
  window.location.reload();
});

if (import.meta.env.PROD && import.meta.env.VITE_HOTJAR) {
  hotjar.initialize({
    id: Number(import.meta.env.VITE_HOTJAR),
    sv: 6,
  });
}

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV || "development",
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "TypeError: NetworkError when attempting to fetch resource.",
      "Failed to fetch",
      "Invalid Token",
    ],
    release: CI_COMMIT_SHORT_SHA,
    // React renders the undefined module before the page unloads.
    beforeSend: event => (isReloadingForNewBuild ? null : event),
  });
  Sentry.setTag("CI_COMMIT_SHORT_SHA", CI_COMMIT_SHORT_SHA);
}

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  <ApolloProvider client={clientNeoAuth}>
    <App />
  </ApolloProvider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
